import { clamp, DEFAULT_PERSPECTIVE_FOV } from "@faro-lotv/lotv";
import { ShaderMaterial, type Texture } from "three";
import { type ShaderMaterialParameters } from "three/src/materials/ShaderMaterial";

const vertexShader = `
varying vec2 vUv;

void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vUv = uv;
}`;

const fragmentShader = `
varying vec2 vUv;

uniform float uXSize;
uniform float uYSize;

// Border half-thickness in UV/angular space. Scaled with the camera zoom so the
// border keeps a constant thickness on screen instead of growing when zooming in.
uniform float uBorderThickness;

uniform sampler2D uGradient;

void main() {

    float minX = uBorderThickness / uXSize;
    float maxX = 1. - minX;

    float minY = uBorderThickness * 2.f / uYSize;
    float maxY = 1. - minY;

    // Sampled before the branch, which is divergent along the border and would leave the texture
    // derivatives undefined.
    vec4 gradient = texture2D(uGradient, vUv);

    if (vUv.x > minX &&
        vUv.x < maxX &&
        vUv.y > minY &&
        vUv.y < maxY) {
        gl_FragColor = vec4(1., 1., 1., 0.2f);
    } else {
        gl_FragColor = gradient;
    }
}
`;

type BoundingBoxRendererMaterialProps = Pick<
  ShaderMaterialParameters,
  "side" | "transparent"
> & {
  xSize: number;
  ySize: number;

  /** The AI gradient to paint the border of the box with. */
  gradient: Texture;
};

/**
 * Border half-thickness (in UV/angular space) shown at the default camera FOV.
 * This is the value the border had before it was made zoom-aware, so the
 * appearance at default zoom is unchanged.
 */
const BASE_BORDER_THICKNESS = 0.001;

/**
 * Clamp bounds for the zoom scale factor applied to {@link BASE_BORDER_THICKNESS}.
 * The lower bound keeps the border visible when zoomed all the way in, the upper
 * bound prevents it from growing beyond its default thickness when zoomed out.
 */
const MIN_ZOOM_SCALE = 0.01;
const MAX_ZOOM_SCALE = 1;

/** The material used for the rendering of the bounding boxes generated by the AI */
export class BoundingBoxRendererMaterial extends ShaderMaterial {
  override uniforms: {
    uXSize: { value: number };
    uYSize: { value: number };
    uBorderThickness: { value: number };
    uGradient: { value: Texture | null };
  } = {
    uXSize: { value: 1 },
    uYSize: { value: 1 },
    uBorderThickness: { value: BASE_BORDER_THICKNESS },
    uGradient: { value: null },
  };

  /** Create the material for the rendering of the bounding box  */
  constructor({
    xSize,
    ySize,
    gradient,
    ...props
  }: BoundingBoxRendererMaterialProps) {
    super({
      ...props,
      vertexShader,
      fragmentShader,
      // The bounding boxes are all rendered on the surface of the unit sphere at
      // the same radius. Where they overlap they share identical depth values,
      // which causes z-fighting (flickering) as the camera moves. Disabling depth
      // testing and writing removes that depth comparison entirely: the overlays
      // simply blend based on their render order, eliminating the flicker.
      depthTest: false,
      depthWrite: false,
    });

    this.uniforms.uXSize.value = xSize;
    this.uniforms.uYSize.value = ySize;
    this.uniforms.uGradient.value = gradient;
  }

  /**
   * Scale the border thickness with the current camera FOV so it stays visually
   * constant on screen. Zooming in reduces the FOV, which shrinks the angular
   * border thickness by the same factor and keeps its on-screen size unchanged.
   *
   * @param fov The current (effective) perspective camera FOV in degrees.
   */
  setZoomScale(fov: number): void {
    const scale = clamp(
      fov / DEFAULT_PERSPECTIVE_FOV,
      MIN_ZOOM_SCALE,
      MAX_ZOOM_SCALE,
    );
    this.uniforms.uBorderThickness.value = BASE_BORDER_THICKNESS * scale;
  }
}
